import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Send, Users, Settings, Lock, UserMinus, UserPlus, Trash2, X, Check, Shield } from "lucide-react";
import { toast } from "sonner";
import { io } from "socket.io-client";
import { api, useAuth } from "../contexts/AuthContext";
import AvatarDisplay from "../components/AvatarDisplay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ICONS = ["🏠", "🎉", "🎮", "🎬", "🎵", "🌙", "☕", "🔥", "⭐", "🌈", "🍕", "🎲"];

function formatTime(ts) {
  try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function SystemMessage({ content }) {
  return (
    <div className="flex justify-center my-2">
      <span className="text-xs text-slate-500 bg-[#141322] px-3 py-1 rounded-full">{content}</span>
    </div>
  );
}

function ChatMsg({ msg, currentUserId }) {
  const isOwn = msg.user_id === currentUserId;
  return (
    <div className={`flex gap-3 msg-enter ${isOwn ? "flex-row-reverse" : ""}`}>
      <AvatarDisplay avatarId={msg.avatar_id} size="sm" className="mt-1 shrink-0" />
      <div className={`max-w-[75%] flex flex-col ${isOwn ? "items-end" : "items-start"}`}>
        <div className={`flex items-baseline gap-2 mb-1 ${isOwn ? "flex-row-reverse" : ""}`}>
          <span className="text-xs font-semibold text-slate-300">{msg.alias}</span>
          <span className="text-[10px] text-slate-600">{formatTime(msg.timestamp)}</span>
        </div>
        <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
          isOwn ? "bg-gradient-to-r from-violet-600/80 to-purple-600/80 text-white rounded-tr-sm"
                : "bg-[#141322] text-slate-200 border border-purple-500/10 rounded-tl-sm"
        }`}>
          {msg.content}
        </div>
      </div>
    </div>
  );
}

// ── Manage modal (creator only) ────────────────────────────────────────────────
function ManageModal({ room, onClose, onRefresh, onDelete }) {
  const [tab, setTab] = useState("members");
  const [friends, setFriends] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [name, setName] = useState(room.name);
  const [icon, setIcon] = useState(room.icon || "🏠");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState("");

  const notify = (msg) => { setNotification(msg); setTimeout(() => setNotification(""), 3000); };

  useEffect(() => {
    api.get("/friends").then(r => {
      const memberIds = new Set(room.members.map(m => m.user_id));
      const pendingIds = new Set((room.pending_invitations || []).map(i => i.invited_user_id));
      setFriends((r.data.friends || []).filter(f => !memberIds.has(f.user_id) && !pendingIds.has(f.user_id)));
    }).catch(() => {});
  }, []);

  const toggleFriend = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleInvite = async () => {
    if (selected.size === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/friends-rooms/${room.room_id}/invite`, { friend_ids: [...selected] });
      notify(data.message);
      setSelected(new Set());
      onRefresh();
    } catch { notify("Failed to send invitations"); }
    setLoading(false);
  };

  const handleRemove = async (memberId) => {
    try {
      await api.delete(`/friends-rooms/${room.room_id}/members/${memberId}`);
      notify("Member removed"); onRefresh();
    } catch { notify("Failed to remove"); }
  };

  const handleSave = async () => {
    try {
      await api.put(`/friends-rooms/${room.room_id}`, { name, icon });
      notify("Room updated"); onRefresh();
    } catch { notify("Failed to update"); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${room.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/friends-rooms/${room.room_id}`); onDelete(); }
    catch { notify("Failed to delete"); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E0E17] border border-purple-500/25 rounded-2xl w-full max-w-md shadow-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-purple-500/15 shrink-0">
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-purple-400" />
            <span className="font-bold text-white font-[Outfit]">Manage Room</span>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        {notification && (
          <div className="mx-5 mt-3 p-2 bg-purple-500/10 border border-purple-500/25 rounded-xl text-purple-300 text-xs text-center">
            {notification}
          </div>
        )}

        <div className="flex gap-1 p-3 border-b border-purple-500/10 shrink-0">
          {[["members", "Members"], ["invite", "Invite"], ["settings", "Settings"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-all ${
                tab === id ? "bg-purple-500/20 text-white" : "text-slate-400 hover:text-white"
              }`}>
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {tab === "members" && (
            <>
              <p className="text-xs text-slate-500 uppercase tracking-wide font-mono">
                Accepted Members ({room.members?.length || 0})
              </p>
              {room.members?.map(m => (
                <div key={m.user_id} className="flex items-center gap-3 p-2 rounded-xl">
                  <AvatarDisplay avatarId={m.avatar_id} size="sm" status={m.is_online ? "online" : "offline"} showStatus />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-white font-medium">{m.alias}</span>
                    {m.role === "creator" && <span className="ml-2 text-[10px] font-mono bg-cyan-500/20 text-cyan-400 px-1.5 rounded-full">YOU</span>}
                  </div>
                  {m.role !== "creator" && (
                    <button onClick={() => handleRemove(m.user_id)} data-testid={`remove-member-${m.user_id}`}
                      className="text-slate-600 hover:text-red-400 transition-colors">
                      <UserMinus className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {(room.pending_invitations?.length || 0) > 0 && (
                <>
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-mono pt-2">
                    Pending Invitations ({room.pending_invitations.length})
                  </p>
                  {room.pending_invitations.map(inv => (
                    <div key={inv.invitation_id} className="flex items-center gap-2 p-2 rounded-xl bg-amber-500/5 border border-amber-500/15">
                      <span className="text-xs text-amber-400 font-mono">PENDING</span>
                      <span className="text-sm text-slate-300 flex-1">{inv.inviter_alias || "—"}</span>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {tab === "invite" && (
            <>
              {selected.size > 0 && (
                <div className="flex items-center justify-between bg-purple-500/10 border border-purple-500/20 rounded-xl p-2">
                  <span className="text-xs text-purple-300">{selected.size} friend{selected.size > 1 ? "s" : ""} selected</span>
                  <button onClick={handleInvite} disabled={loading}
                    className="px-3 py-1 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-xs font-semibold rounded-lg hover:opacity-90 disabled:opacity-50">
                    {loading ? "Sending..." : "Send Invites"}
                  </button>
                </div>
              )}
              {friends.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">All your friends have been invited already.</div>
              ) : friends.map(f => {
                const isSel = selected.has(f.user_id);
                return (
                  <button key={f.user_id} onClick={() => toggleFriend(f.user_id)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl border transition-all text-left ${
                      isSel ? "border-purple-500/50 bg-purple-500/10" : "border-slate-700 hover:border-purple-500/30"
                    }`}>
                    <AvatarDisplay avatarId={f.avatar_id} size="sm" />
                    <span className="flex-1 text-sm text-white">{f.alias}</span>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSel ? "border-purple-400 bg-purple-500" : "border-slate-600"}`}>
                      {isSel && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {tab === "settings" && (
            <div className="space-y-4">
              <div>
                <label className="block text-xs text-slate-400 mb-2 uppercase tracking-wide">Icon</label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(ic => (
                    <button key={ic} onClick={() => setIcon(ic)}
                      className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center border transition-all ${
                        icon === ic ? "border-cyan-400 bg-cyan-500/15 scale-110" : "border-slate-700"
                      }`}>{ic}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-2 uppercase tracking-wide">Room Name</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  className="w-full bg-[#141322] border border-slate-700 rounded-xl py-2.5 px-4 text-white text-sm focus:outline-none focus:border-purple-500" />
              </div>
              <button onClick={handleSave} className="w-full py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold rounded-xl text-sm hover:opacity-90">
                Save Changes
              </button>
              <button onClick={handleDelete} className="w-full py-2.5 border border-red-500/30 text-red-400 rounded-xl text-sm hover:bg-red-500/10 transition-colors flex items-center justify-center gap-2">
                <Trash2 className="w-4 h-4" /> Delete Room
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  FRIENDS ROOM PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function FriendsRoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [input, setInput] = useState("");
  const [showSidebar, setShowSidebar] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [notification, setNotification] = useState("");
  const [typingUsers, setTypingUsers] = useState([]);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimer = useRef(null);

  const notify = (msg) => { setNotification(msg); setTimeout(() => setNotification(""), 3000); };

  const fetchRoom = async () => {
    try { const { data } = await api.get(`/friends-rooms/${roomId}`); setRoom(data); setMembers(data.members || []); }
    catch { navigate("/lobby"); }
  };

  useEffect(() => {
    fetchRoom();
    api.get(`/friends-rooms/${roomId}/messages`).then(r => setMessages(r.data)).catch(() => {});

    socketRef.current = io(BACKEND_URL, { path: "/api/socket.io", withCredentials: true, transports: ["websocket", "polling"] });

    socketRef.current.on("connect", () => {
      socketRef.current.emit("join_friends_room", { room_id: roomId });
    });

    socketRef.current.on("fr_members", ({ members: m }) => setMembers(m));
    socketRef.current.on("new_fr_message", (msg) => setMessages(prev => [...prev, msg]));
    socketRef.current.on("fr_system_message", ({ content }) => {
      setMessages(prev => [...prev, { msg_id: `sys_${Date.now()}_${Math.random().toString(36).slice(2,5)}`, is_system: true, content, timestamp: new Date().toISOString() }]);
    });
    socketRef.current.on("user_typing", ({ alias, is_typing }) => {
      setTypingUsers(prev => is_typing ? [...new Set([...prev, alias])] : prev.filter(a => a !== alias));
    });
    socketRef.current.on("fr_kicked", ({ room_id }) => {
      if (room_id === roomId) { notify("You were removed from this room."); setTimeout(() => navigate("/lobby"), 2000); }
    });
    socketRef.current.on("fr_deleted", ({ room_id }) => {
      if (room_id === roomId) { notify("This room was deleted."); setTimeout(() => navigate("/lobby"), 2000); }
    });
    socketRef.current.on("new_friend_request", ({ from_alias }) => {
      toast(`${from_alias} wants to be friends`, {
        description: "Tap to view your friend requests",
        action: { label: "View", onClick: () => navigate("/friends") },
        duration: 8000,
        icon: "👋",
      });
    });

    return () => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("leave_friends_room", { room_id: roomId });
        socketRef.current.disconnect();
      }
    };
  }, [roomId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = (e) => {
    e.preventDefault();
    if (!input.trim() || !socketRef.current?.connected) return;
    socketRef.current.emit("send_fr_message", { room_id: roomId, content: input.trim() });
    setInput("");
    clearTimeout(typingTimer.current);
    socketRef.current.emit("fr_typing", { room_id: roomId, is_typing: false });
  };

  const handleInputChange = (val) => {
    setInput(val);
    if (socketRef.current?.connected) {
      socketRef.current.emit("fr_typing", { room_id: roomId, is_typing: val.length > 0 });
      clearTimeout(typingTimer.current);
      if (val.length > 0) typingTimer.current = setTimeout(() => socketRef.current?.emit("fr_typing", { room_id: roomId, is_typing: false }), 2000);
    }
  };

  return (
    <div className="h-screen bg-[#07070B] flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-14 bg-[#0E0E17]/90 backdrop-blur-xl border-b border-purple-500/20 flex items-center px-4 gap-3 shrink-0">
        <button data-testid="back-from-fr-btn" onClick={() => navigate("/lobby")} className="text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <span className="text-xl shrink-0">{room?.icon || "🏠"}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <h1 className="font-bold text-white font-[Outfit] truncate">{room?.name || "Loading..."}</h1>
              <Lock className="w-3 h-3 text-purple-400 shrink-0" />
            </div>
            <p className="text-[11px] text-slate-500">Friends Room · {members.length} member{members.length !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {room?.is_creator && (
            <button data-testid="fr-manage-btn" onClick={() => setShowManage(true)}
              className="text-slate-400 hover:text-purple-400 transition-colors">
              <Settings className="w-5 h-5" />
            </button>
          )}
          <button data-testid="fr-toggle-members-btn" onClick={() => setShowSidebar(v => !v)}
            className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors">
            <Users className="w-5 h-5" />
            <span className="text-xs">{members.length}</span>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Messages */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-thin" data-testid="fr-messages-container">
            {messages.map(msg =>
              msg.is_system
                ? <SystemMessage key={msg.msg_id} content={msg.content} />
                : <ChatMsg key={msg.msg_id} msg={msg} currentUserId={user?.user_id} />
            )}
            {typingUsers.length > 0 && (
              <div className="flex items-center gap-2 text-xs text-slate-500 pl-2">
                <div className="flex gap-1">
                  {[0, 1, 2].map(i => <span key={i} className="w-1.5 h-1.5 rounded-full bg-slate-500 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />)}
                </div>
                {typingUsers.join(", ")} typing
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <AnimatePresence>
            {notification && (
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                className="mx-4 mb-2 p-2.5 bg-[#141322] border border-purple-500/30 rounded-xl text-sm text-slate-300 text-center">
                {notification}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Input */}
          <div className="p-3 bg-[#0B0B12] border-t border-purple-500/15">
            <form onSubmit={sendMessage} className="flex gap-2">
              <AvatarDisplay avatarId={user?.avatar_id} size="sm" className="mt-0.5 shrink-0" />
              <input
                data-testid="fr-message-input"
                value={input}
                onChange={e => handleInputChange(e.target.value)}
                placeholder="Message your friends..."
                className="flex-1 bg-[#141322] border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:outline-none focus:border-purple-500 transition-colors"
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) sendMessage(e); }}
              />
              <button data-testid="fr-send-btn" type="submit" disabled={!input.trim()}
                className="w-10 h-10 rounded-xl bg-gradient-to-r from-violet-600 to-cyan-500 flex items-center justify-center text-white disabled:opacity-40 hover:opacity-90 active:scale-95 transition-all">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Members sidebar */}
        <AnimatePresence>
          {showSidebar && (
            <motion.div initial={{ width: 0, opacity: 0 }} animate={{ width: 240, opacity: 1 }} exit={{ width: 0, opacity: 0 }}
              className="bg-[#0E0E17] border-l border-purple-500/15 overflow-hidden shrink-0">
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-white font-[Outfit]">Members</span>
                  <button onClick={() => setShowSidebar(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-2">
                  {members.map(m => (
                    <div key={m.user_id} data-testid={`fr-member-${m.user_id}`} className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-[#141322] transition-colors">
                      <AvatarDisplay avatarId={m.avatar_id} size="sm" status={m.is_online ? "online" : "offline"} showStatus />
                      <div className="min-w-0">
                        <p className="text-sm text-slate-200 truncate">{m.alias}{m.user_id === user?.user_id ? " (you)" : ""}</p>
                        {m.role === "creator" && <p className="text-[10px] text-purple-400 font-mono">Creator</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {showManage && room && (
        <ManageModal room={room} onClose={() => setShowManage(false)}
          onRefresh={() => { fetchRoom(); setShowManage(false); }}
          onDelete={() => navigate("/lobby")} />
      )}
    </div>
  );
}
